import { AlertTriangle, ArrowRight, Calendar, TrendingDown, TrendingUp } from 'lucide-react';
import { expandSectorTickersInText } from '@/utils/marketPredictionCopy';
import type {
  AnalogueCatalyst,
  AnalogueCatalystCase,
  MarketAnalogueAnalysis,
} from '@/services/api';

function formatPct(value: number) {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

function pctChipClass(value: number) {
  if (value > 0) return 'bg-emerald-50 text-emerald-800';
  if (value < 0) return 'bg-rose-50 text-rose-800';
  return 'bg-gray-100 text-gray-600';
}

function formatDateLabel(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    const date = new Date(`${value}T12:00:00`);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return value;
  }
}

function prose(text: string | null | undefined): string | null {
  if (!text?.trim()) return null;
  return expandSectorTickersInText(text.trim());
}

function importanceBadge(importance: string | null | undefined) {
  const level = (importance ?? '').toLowerCase();
  if (level === 'critical') return 'bg-rose-50 text-rose-800';
  if (level === 'high') return 'bg-amber-50 text-amber-800';
  return 'bg-gray-100 text-gray-600';
}

function outcomeBadge(direction: string | null | undefined) {
  const d = (direction ?? '').toLowerCase();
  if (d === 'bullish') return 'bg-emerald-50 text-emerald-800';
  if (d === 'bearish') return 'bg-rose-50 text-rose-800';
  return 'bg-gray-100 text-gray-600';
}

function StatChips({ stats, keys }: { stats: Record<string, number> | null | undefined; keys: string[] }) {
  if (!stats) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {keys.map((key) => {
        const val = stats[key];
        if (typeof val !== 'number' || !Number.isFinite(val)) return null;
        return (
          <span key={key} className={`rounded-lg px-2.5 py-1 text-xs font-medium ${pctChipClass(val)}`}>
            {key} {formatPct(val)}
          </span>
        );
      })}
    </div>
  );
}

function BulletList({ items, tone = 'neutral' }: { items: string[]; tone?: 'neutral' | 'positive' | 'negative' }) {
  if (!items.length) return null;
  const dot =
    tone === 'positive' ? 'text-emerald-500' : tone === 'negative' ? 'text-rose-500' : 'text-gray-400';
  return (
    <ul className="space-y-2 text-sm text-gray-700 leading-relaxed">
      {items.map((item) => (
        <li key={item} className="flex gap-2">
          <span className={`${dot} shrink-0`}>·</span>
          <span>{prose(item)}</span>
        </li>
      ))}
    </ul>
  );
}

function ScenarioBranch({
  label,
  tone,
  scenario,
}: {
  label: string;
  tone: 'bull' | 'bear';
  scenario: AnalogueCatalystCase | null | undefined;
}) {
  if (!scenario) return null;
  const border = tone === 'bull' ? 'border-emerald-100 bg-emerald-50/40' : 'border-rose-100 bg-rose-50/40';
  const title = tone === 'bull' ? 'text-emerald-900' : 'text-rose-900';
  const Icon = tone === 'bull' ? TrendingUp : TrendingDown;

  return (
    <div className={`rounded-xl border p-4 ${border}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`h-4 w-4 ${tone === 'bull' ? 'text-emerald-600' : 'text-rose-600'}`} />
        <p className={`text-sm font-medium ${title}`}>{label}</p>
        {scenario.trigger ? (
          <span className="text-xs text-gray-500">— {scenario.trigger}</span>
        ) : null}
      </div>
      {scenario.market_path ? (
        <p className="text-sm text-gray-700 leading-relaxed mb-2">{prose(scenario.market_path)}</p>
      ) : null}
      {scenario.implication ? (
        <p className="text-xs text-gray-500 leading-relaxed mb-1">{prose(scenario.implication)}</p>
      ) : null}
      {scenario.analogue ? (
        <p className="text-xs text-gray-500 italic">{prose(scenario.analogue)}</p>
      ) : null}
      {scenario.spy_target_short_term || scenario.spy_target_4_to_8_weeks ? (
        <p className="text-xs font-medium text-gray-600 mt-2">
          {scenario.spy_target_short_term ?? scenario.spy_target_4_to_8_weeks}
          {scenario.probability ? ` · ${scenario.probability}` : ''}
        </p>
      ) : null}
    </div>
  );
}

function CatalystCard({ catalyst }: { catalyst: AnalogueCatalyst }) {
  return (
    <div className="rounded-2xl border border-gray-100 p-5 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="text-xs font-medium text-gray-400">#{catalyst.rank}</span>
            <h5 className="text-lg font-medium text-gray-900">{catalyst.event}</h5>
            {catalyst.importance ? (
              <span className={`rounded-lg px-2 py-0.5 text-xs capitalize ${importanceBadge(catalyst.importance)}`}>
                {catalyst.importance}
              </span>
            ) : null}
          </div>
          {catalyst.date ? (
            <p className="text-sm text-gray-500 flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              {catalyst.date}
            </p>
          ) : null}
        </div>
      </div>

      {catalyst.question ? (
        <p className="text-base text-gray-800 font-medium mb-3">{catalyst.question}</p>
      ) : null}
      {catalyst.context ? (
        <p className="text-sm text-gray-600 leading-relaxed mb-4">{prose(catalyst.context)}</p>
      ) : null}

      <div className="grid gap-3 md:grid-cols-2">
        <ScenarioBranch label="If it goes right" tone="bull" scenario={catalyst.bull_case} />
        <ScenarioBranch label="If it goes wrong" tone="bear" scenario={catalyst.bear_case} />
      </div>
    </div>
  );
}

function ProbabilityBar({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | null | undefined;
  tone: 'emerald' | 'amber' | 'rose' | 'gray';
}) {
  if (value == null) return null;
  const pct = Math.round(value * 100);
  const fill =
    tone === 'emerald'
      ? 'bg-emerald-500'
      : tone === 'amber'
        ? 'bg-amber-500'
        : tone === 'rose'
          ? 'bg-rose-500'
          : 'bg-gray-400';
  return (
    <div>
      <div className="flex justify-between text-xs text-gray-600 mb-1">
        <span>{label}</span>
        <span>{pct}%</span>
      </div>
      <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
        <div className={`h-full rounded-full ${fill}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

type Props = {
  analysis: MarketAnalogueAnalysis;
  className?: string;
};

export function MarketAnalogueAnalysisPanel({ analysis, className = '' }: Props) {
  const ctx = analysis.context;
  const framework =
    analysis.analogue_framework?.three_lens_approach ??
    analysis.analogue_framework?.two_lens_approach;
  const situational = analysis.situational_analogue;
  const numerical = analysis.numerical_analogue;
  const regime = analysis.regime_analogue;
  const danger = analysis.dangerous_upcoming_window ?? analysis.danger_template;
  const synthesis = analysis.synthesis;
  const catalysts = analysis.three_catalysts;
  const verdict = analysis.composite_verdict;

  const market = ctx?.todays_market ?? {};
  const indexKeys = ['DIA', 'SPY', 'QQQ', 'IWM'] as const;
  const sectors = ctx?.sector_performance ?? {};
  const sectorRows = Object.entries(sectors).sort(([, a], [, b]) => b - a);
  const hasProbabilityBars =
    verdict?.base_case_probability != null ||
    verdict?.consolidation_case_probability != null ||
    verdict?.bear_case_probability != null;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Setup */}
      <section className="rounded-3xl border border-gray-200 bg-white p-8 md:p-10 lg:p-12 shadow-sm">
        <p className="text-sm font-medium text-gray-500 mb-2">The setup</p>
        <h3 className="text-3xl md:text-4xl text-gray-900 tracking-tight mb-6">
          {ctx?.date_label ?? 'Market outlook'}
        </h3>

        {ctx?.situation_summary ? (
          <p className="max-w-5xl text-lg md:text-xl text-gray-800 leading-[1.75] mb-8">
            {prose(ctx.situation_summary)}
          </p>
        ) : null}

        <div className="border-t border-gray-100 pt-6 mb-6">
          <p className="text-sm font-medium text-gray-500 mb-3">Today&apos;s market reaction</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {indexKeys.map((key) => {
              const val = market[key];
              if (typeof val !== 'number') return null;
              return (
                <span key={key} className={`rounded-lg px-3 py-1.5 text-sm font-medium ${pctChipClass(val)}`}>
                  {key} {formatPct(val)}
                </span>
              );
            })}
          </div>
          {sectorRows.length > 0 ? (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {sectorRows.slice(0, 6).map(([sector, pct]) => (
                <div
                  key={sector}
                  className="flex items-center justify-between rounded-xl bg-gray-50 px-4 py-2.5 text-sm"
                >
                  <span className="text-gray-700">{sector.replace(/_/g, ' ')}</span>
                  <span className={`font-medium ${pct >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {formatPct(pct)}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        {framework?.description ? (
          <div className="rounded-2xl bg-gray-50 p-5 border border-gray-100">
            <p className="text-sm font-medium text-gray-900 mb-2">
              {analysis.analogue_framework?.three_lens_approach
                ? 'Three lenses, three messages'
                : 'Two lenses, two messages'}
            </p>
            <p className="text-sm text-gray-700 leading-relaxed mb-3">{prose(framework.description)}</p>
            <div className="grid gap-3 md:grid-cols-3 text-sm">
              {framework.situational_analogue_purpose ? (
                <div>
                  <p className="font-medium text-gray-900 mb-1">Situational</p>
                  <p className="text-gray-600 leading-relaxed">{prose(framework.situational_analogue_purpose)}</p>
                </div>
              ) : null}
              {framework.numerical_analogue_purpose ? (
                <div>
                  <p className="font-medium text-gray-900 mb-1">Mechanical</p>
                  <p className="text-gray-600 leading-relaxed">{prose(framework.numerical_analogue_purpose)}</p>
                </div>
              ) : null}
              {framework.regime_analogue_purpose ? (
                <div>
                  <p className="font-medium text-gray-900 mb-1">Regime / macro</p>
                  <p className="text-gray-600 leading-relaxed">{prose(framework.regime_analogue_purpose)}</p>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </section>

      {/* Analogues */}
      <div className={`grid gap-6 ${regime ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}>
        {situational ? (
          <section className="rounded-3xl border border-gray-200 bg-white p-8 shadow-sm">
            <p className="text-sm font-medium text-emerald-700 mb-1">Situational analogue</p>
            <h4 className="text-xl text-gray-900 mb-1">{formatDateLabel(situational.date)}</h4>
            <p className="text-lg font-medium text-gray-800 mb-4">{situational.title}</p>

            <div className="flex flex-wrap gap-2 mb-4">
              {situational.similarity_label ? (
                <span className="rounded-lg bg-emerald-50 px-2.5 py-1 text-xs text-emerald-800">
                  {situational.similarity_label} match
                  {situational.similarity_score != null
                    ? ` · ${(situational.similarity_score * 100).toFixed(0)}%`
                    : ''}
                </span>
              ) : null}
              {situational.outcome ? (
                <span className={`rounded-lg px-2.5 py-1 text-xs ${outcomeBadge(situational.outcome_direction)}`}>
                  {situational.outcome}
                </span>
              ) : null}
            </div>

            {situational.context ? (
              <p className="text-sm text-gray-700 leading-relaxed mb-4">{prose(situational.context)}</p>
            ) : null}

            <StatChips stats={situational.day_stats ?? undefined} keys={['SPY', 'QQQ', 'DIA', 'IWM', 'USO']} />

            {situational.fed_context ? (
              <p className="text-xs text-gray-500 mt-3 mb-4">{situational.fed_context}</p>
            ) : null}

            {situational.similarities?.length ? (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-sm font-medium text-gray-900 mb-2">Why it rhymes</p>
                <BulletList items={situational.similarities} tone="positive" />
              </div>
            ) : null}

            {situational.what_happened_next ? (
              <div className="mt-4 rounded-xl bg-emerald-50/60 border border-emerald-100 p-4">
                <p className="text-sm font-medium text-emerald-900 mb-2">What happened next</p>
                <p className="text-sm text-emerald-800 leading-relaxed">{prose(situational.what_happened_next)}</p>
              </div>
            ) : null}

            {situational.forward_implication ? (
              <div className="mt-4 flex gap-2 items-start">
                <ArrowRight className="h-4 w-4 text-gray-400 shrink-0 mt-1" />
                <p className="text-sm font-medium text-gray-900 leading-relaxed">
                  {prose(situational.forward_implication)}
                </p>
              </div>
            ) : null}
          </section>
        ) : null}

        {numerical ? (
          <section className="rounded-3xl border border-gray-200 bg-white p-8 shadow-sm">
            <p className="text-sm font-medium text-blue-700 mb-1">Mechanical analogue</p>
            <h4 className="text-xl text-gray-900 mb-1">
              {numerical.date ? formatDateLabel(String(numerical.date)) : numerical.pattern}
            </h4>
            {numerical.pattern ? (
              <p className="text-lg font-medium text-gray-800 mb-4">{numerical.pattern}</p>
            ) : null}

            {numerical.pattern_verdict ? (
              <span className="inline-block rounded-lg bg-blue-50 px-3 py-1 text-sm font-medium text-blue-800 mb-4">
                {numerical.pattern_verdict}
              </span>
            ) : null}

            {numerical.context ? (
              <p className="text-sm text-gray-700 leading-relaxed mb-4">{prose(numerical.context)}</p>
            ) : null}

            {numerical.pattern_explanation ? (
              <p className="text-sm text-gray-700 leading-relaxed mb-4">{prose(numerical.pattern_explanation)}</p>
            ) : null}

            {numerical.historical_examples?.length ? (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-sm font-medium text-gray-900 mb-2">Same pattern before</p>
                <BulletList items={numerical.historical_examples} />
              </div>
            ) : null}

            {numerical.what_this_means_short_term ? (
              <div className="mt-4 rounded-xl bg-blue-50/60 border border-blue-100 p-4">
                <p className="text-sm font-medium text-blue-900 mb-2">Short-term read</p>
                <p className="text-sm text-blue-800 leading-relaxed">
                  {prose(numerical.what_this_means_short_term)}
                </p>
              </div>
            ) : null}
          </section>
        ) : null}

        {regime ? (
          <section className="rounded-3xl border border-gray-200 bg-white p-8 shadow-sm">
            <p className="text-sm font-medium text-violet-700 mb-1">Regime / macro analogue</p>
            <h4 className="text-xl text-gray-900 mb-1">{formatDateLabel(regime.date)}</h4>
            <p className="text-lg font-medium text-gray-800 mb-4">{regime.title}</p>

            <div className="flex flex-wrap gap-2 mb-4">
              {regime.similarity_label ? (
                <span className="rounded-lg bg-violet-50 px-2.5 py-1 text-xs text-violet-800">
                  {regime.similarity_label}
                </span>
              ) : null}
              {regime.outcome ? (
                <span className={`rounded-lg px-2.5 py-1 text-xs ${outcomeBadge(regime.outcome_direction)}`}>
                  {regime.outcome}
                </span>
              ) : null}
            </div>

            {regime.context ? (
              <p className="text-sm text-gray-700 leading-relaxed mb-4">{prose(regime.context)}</p>
            ) : null}

            {regime.what_happened_next ? (
              <div className="mt-4 rounded-xl bg-violet-50/60 border border-violet-100 p-4">
                <p className="text-sm font-medium text-violet-900 mb-2">What happened next</p>
                <p className="text-sm text-violet-800 leading-relaxed">{prose(regime.what_happened_next)}</p>
              </div>
            ) : null}

            {regime.forward_implication ? (
              <div className="mt-4 flex gap-2 items-start">
                <ArrowRight className="h-4 w-4 text-gray-400 shrink-0 mt-1" />
                <p className="text-sm font-medium text-gray-900 leading-relaxed">
                  {prose(regime.forward_implication)}
                </p>
              </div>
            ) : null}
          </section>
        ) : null}
      </div>

      {/* Synthesis */}
      {synthesis?.summary ? (
        <section className="rounded-3xl border border-gray-200 bg-white p-8 md:p-10 shadow-sm">
          <h4 className="text-2xl text-gray-900 mb-4">Cross-analog synthesis</h4>
          <p className="text-gray-700 leading-relaxed mb-6">{prose(synthesis.summary)}</p>
          {synthesis.sectors_benefited_across_analogs?.length ? (
            <div className="mb-6">
              <p className="text-sm font-medium text-emerald-800 mb-3">Sectors that held up across analogs</p>
              <div className="space-y-3">
                {synthesis.sectors_benefited_across_analogs.map((row) => (
                  <div key={row.sector} className="rounded-xl bg-emerald-50/50 border border-emerald-100 p-4">
                    <p className="font-medium text-emerald-900 mb-1">{row.sector}</p>
                    {row.why ? <p className="text-sm text-emerald-800 leading-relaxed">{prose(row.why)}</p> : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          {synthesis.sectors_harmed_across_analogs?.length ? (
            <div>
              <p className="text-sm font-medium text-rose-800 mb-3">Sectors that struggled across analogs</p>
              <div className="space-y-3">
                {synthesis.sectors_harmed_across_analogs.map((row) => (
                  <div key={row.sector} className="rounded-xl bg-rose-50/50 border border-rose-100 p-4">
                    <p className="font-medium text-rose-900 mb-1">{row.sector}</p>
                    {row.why ? <p className="text-sm text-rose-800 leading-relaxed">{prose(row.why)}</p> : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </section>
      ) : null}

      {/* Danger window / template */}
      {danger ? (
        <section className="rounded-3xl border border-rose-200 bg-rose-50/30 p-8 shadow-sm">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-rose-800 mb-1">Dangerous upcoming window</p>
              <h4 className="text-xl text-gray-900">
                {'date' in danger && danger.date
                  ? `${formatDateLabel(danger.date)} — ${danger.title ?? ''}`
                  : danger.title ?? 'Upcoming risk window'}
              </h4>
            </div>
          </div>

          {'summary' in danger && danger.summary ? (
            <p className="text-sm text-gray-700 leading-relaxed mb-4">{prose(danger.summary)}</p>
          ) : null}

          {'context' in danger && danger.context ? (
            <p className="text-sm text-gray-700 leading-relaxed mb-4">{prose(danger.context)}</p>
          ) : null}

          {'what_happened_next' in danger && danger.what_happened_next ? (
            <p className="text-sm text-rose-800 leading-relaxed mb-4 font-medium">
              {prose(danger.what_happened_next)}
            </p>
          ) : null}

          {'events' in danger && danger.events?.length ? (
            <ul className="space-y-2 mb-4 text-sm text-gray-700">
              {danger.events.map((event) => (
                <li key={event.event} className="flex gap-2">
                  <span className="text-rose-500 shrink-0">·</span>
                  <span>
                    <strong>{event.event}</strong>
                    {event.why_it_matters ? ` — ${event.why_it_matters}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}

          {'verdict' in danger && danger.verdict ? (
            <p className="text-sm font-medium text-rose-900 border-t border-rose-100 pt-4">
              {prose(danger.verdict)}
            </p>
          ) : null}

          {'key_differences_from_today' in danger && danger.key_differences_from_today?.length ? (
            <div className="mb-4 mt-4">
              <p className="text-sm font-medium text-gray-900 mb-2">Why today is different</p>
              <BulletList items={danger.key_differences_from_today} />
            </div>
          ) : null}

          {'why_this_matters' in danger && danger.why_this_matters ? (
            <p className="text-sm text-gray-700 leading-relaxed border-t border-rose-100 pt-4">
              {prose(danger.why_this_matters)}
            </p>
          ) : null}
        </section>
      ) : null}

      {/* Three catalysts */}
      {catalysts?.catalysts?.length ? (
        <section className="rounded-3xl border border-gray-200 bg-white p-8 md:p-10 shadow-sm">
          <h4 className="text-2xl text-gray-900 mb-2">What determines which way this goes</h4>
          {catalysts.description ? (
            <p className="text-gray-600 mb-6 leading-relaxed">{prose(catalysts.description)}</p>
          ) : null}
          <div className="space-y-4">
            {catalysts.catalysts.map((c) => (
              <CatalystCard key={c.rank ?? c.event} catalyst={c} />
            ))}
          </div>
        </section>
      ) : null}

      {/* Verdict */}
      {verdict ? (
        <section className="rounded-3xl border border-gray-900 bg-gray-900 text-white p-8 md:p-10 shadow-sm">
          <p className="text-sm font-medium text-gray-400 mb-3">The read</p>

          {verdict.summary ? (
            <p className="text-lg md:text-xl leading-[1.75] text-gray-100 mb-6">{prose(verdict.summary)}</p>
          ) : null}

          {hasProbabilityBars ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-6">
              <ProbabilityBar label="Bull continuation" value={verdict.base_case_probability} tone="emerald" />
              <ProbabilityBar label="Consolidation" value={verdict.consolidation_case_probability} tone="amber" />
              <ProbabilityBar label="Bear / deal fails" value={verdict.bear_case_probability} tone="rose" />
              <ProbabilityBar label="Warsh shock" value={verdict.warsh_shock_probability} tone="gray" />
            </div>
          ) : verdict.risk_skew || verdict.base_case_direction ? (
            <div className="flex flex-wrap gap-2 mb-6">
              {verdict.base_case_direction ? (
                <span className="rounded-lg bg-gray-700 px-3 py-1 text-sm capitalize text-gray-100">
                  Base case: {verdict.base_case_direction}
                </span>
              ) : null}
              {verdict.risk_skew ? (
                <span className="rounded-lg bg-gray-700 px-3 py-1 text-sm text-gray-100">
                  Risk skew: {verdict.risk_skew}
                </span>
              ) : null}
            </div>
          ) : null}

          {verdict.key_branching_point ? (
            <p className="text-sm text-gray-300 mb-4">
              <span className="text-gray-500">Branching point:</span> {verdict.key_branching_point}
            </p>
          ) : null}

          {verdict.watch_list?.length ? (
            <div className="border-t border-gray-700 pt-5">
              <p className="text-sm font-medium text-gray-400 mb-3">Watch list</p>
              <ul className="space-y-2 text-sm text-gray-200">
                {verdict.watch_list.map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="text-gray-500">·</span>
                    <span>{prose(item)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {verdict.linkedin_hook ? (
            <p className="mt-6 pt-5 border-t border-gray-700 text-base italic text-gray-300 leading-relaxed">
              {prose(verdict.linkedin_hook)}
            </p>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
